import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Building2,
  History,
  ChevronDown,
  ChevronRight,
  Users,
  Loader2,
} from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
// Read-only diagnostics page — gated at 'read' so Viewers can access it. Must stay
// aligned with the sidebar entry in components/layout/Sidebar.tsx (requiredPermission: 'read').
import { ViewerGuard } from '@/components/guards/ViewerGuard';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { StatusBadge } from '@/components/common/StatusBadge';
import { UserDetailDialog } from '@/components/tools/UserDetailDialog';
import { UserStatusBadge } from '@/components/tools/UserStatusBadge';
import { customerIntelApi, customerIntelKeys } from '@/lib/api';
import type { AmplifyOrgUser } from '@/types';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'invited', label: 'Invited' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'removed', label: 'Removed' },
] as const;

export const Route = createFileRoute('/tools/amplify-org')({
  component: AmplifyOrgPage,
});

function AmplifyOrgPage() {
  const [uuid, setUuid] = useState('');
  const [searchUuid, setSearchUuid] = useState('');
  const [uuidError, setUuidError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [emailFilter, setEmailFilter] = useState('');
  const [page, setPage] = useState(1);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AmplifyOrgUser | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const orgQuery = useQuery({
    queryKey: customerIntelKeys.amplifyOrg(searchUuid),
    queryFn: () => customerIntelApi.getAmplifyOrg(searchUuid),
    enabled: searchUuid.length > 0,
  });

  const usersFilters = {
    status: statusFilter !== 'all' ? statusFilter : undefined,
    email: emailFilter.trim() || undefined,
    page,
  };

  const usersQuery = useQuery({
    queryKey: customerIntelKeys.orgUsers(searchUuid, usersFilters),
    queryFn: () => customerIntelApi.getOrgUsers(searchUuid, usersFilters),
    enabled: orgQuery.data?.found === true,
    placeholderData: (prev) => prev,
  });

  const emailTrimmed = emailFilter.trim().toLowerCase();
  const displayedUsers = emailTrimmed
    ? (usersQuery.data?.users ?? []).filter(
        (u) =>
          u.email?.toLowerCase().includes(emailTrimmed) ||
          u.name?.toLowerCase().includes(emailTrimmed),
      )
    : (usersQuery.data?.users ?? []);

  function handleLookup() {
    const trimmed = uuid.trim();
    if (!trimmed) {
      setUuidError('Organization UUID is required');
      return;
    }
    if (!UUID_REGEX.test(trimmed)) {
      setUuidError('Invalid UUID format');
      return;
    }
    setUuidError(null);
    setPage(1);
    setHistoryOpen(false);

    // Re-submitting the same UUID would no-op setSearchUuid, leaving both queries
    // serving cached data. Explicitly refetch both so the button always feels live.
    if (searchUuid === trimmed) {
      void orgQuery.refetch();
      void usersQuery.refetch();
    } else {
      setSearchUuid(trimmed);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleLookup();
  }

  function handleStatusChange(value: string) {
    setStatusFilter(value);
    setPage(1);
  }

  function handleEmailChange(value: string) {
    setEmailFilter(value);
    setPage(1);
  }

  function handleUserClick(user: AmplifyOrgUser) {
    setSelectedUser(user);
    setDialogOpen(true);
  }

  const orgData = orgQuery.data;

  return (
    <AppLayout>
      <ViewerGuard>
        <PageHeader
          title="Amplify Org Lookup"
          description="Look up an Amplify organization, its modules, users, and migration history"
          breadcrumbs={[{ label: 'Tools' }, { label: 'Amplify Org' }]}
        />

        <div className="space-y-6">
          {/* Search */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label htmlFor="org-uuid">Organization UUID</Label>
                  <Input
                    id="org-uuid"
                    placeholder="e.g. a1b2c3d4-e5f6-7890-abcd-ef1234567890"
                    value={uuid}
                    onChange={(e) => setUuid(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="font-mono"
                  />
                </div>
                <Button onClick={handleLookup} disabled={orgQuery.isLoading}>
                  {orgQuery.isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  {orgQuery.isLoading ? 'Looking up...' : 'Look Up'}
                </Button>
              </div>
              {uuidError && (
                <p className="text-sm text-destructive mt-2">{uuidError}</p>
              )}
            </CardContent>
          </Card>

          {/* Error */}
          {orgQuery.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                {(orgQuery.error as Error)?.message ??
                  'Failed to fetch organization data'}
              </AlertDescription>
            </Alert>
          )}

          {/* Not Found */}
          {orgData && !orgData.found && (
            <Alert>
              <AlertDescription>
                Organization not found. Please check the UUID and try again.
              </AlertDescription>
            </Alert>
          )}

          {/* Org Summary */}
          {orgData?.found && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <CardTitle>
                        {orgData.name ?? 'Unknown Organization'}
                      </CardTitle>
                      <p className="font-mono text-xs text-muted-foreground mt-1">
                        {orgData.uuid}
                      </p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Owner</span>
                    <p className="font-medium">
                      {[orgData.ownerFirstName, orgData.ownerLastName]
                        .filter(Boolean)
                        .join(' ') || '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email</span>
                    <p className="font-medium">{orgData.ownerEmail ?? '—'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Modules</span>
                    {orgData.modules.length === 0 ? (
                      <p className="font-medium">—</p>
                    ) : (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {orgData.modules.map((m) => (
                          <Badge key={m.id} variant="outline" className="text-xs">
                            {m.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Users */}
          {orgData?.found && (
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Users
                    {usersQuery.data && (
                      <span className="text-muted-foreground font-normal text-sm">
                        ({usersQuery.data.totalCount} total)
                      </span>
                    )}
                  </CardTitle>
                  <div className="flex gap-2">
                    <>
                      <label htmlFor="user-email-filter" className="sr-only">
                        Filter by email
                      </label>
                      <Input
                        id="user-email-filter"
                        placeholder="Filter by email..."
                        value={emailFilter}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        className="w-48"
                      />
                    </>
                    <Select
                      value={statusFilter}
                      onValueChange={handleStatusChange}
                    >
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {usersQuery.isLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : usersQuery.isError ? (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {(usersQuery.error as Error)?.message ??
                        'Failed to fetch users'}
                    </AlertDescription>
                  </Alert>
                ) : displayedUsers.length === 0 ? (
                  <EmptyState
                    title="No users found"
                    description="Try adjusting your filters."
                    variant="search"
                  />
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead aria-label="Actions"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayedUsers.map((user) => (
                          <TableRow key={user.uuid}>
                            <TableCell className="font-medium">
                              {user.name}
                            </TableCell>
                            <TableCell>{user.email ?? '—'}</TableCell>
                            <TableCell>
                              <UserStatusBadge status={user.status} />
                            </TableCell>
                            <TableCell>
                              {user.organizationOwner ? (
                                <Badge variant="info">Owner</Badge>
                              ) : user.universalAdmin ? (
                                <Badge variant="warning">Admin</Badge>
                              ) : (
                                <span className="text-muted-foreground">
                                  User
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUserClick(user)}
                              >
                                Details →
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Page {usersQuery.data?.currentPage} of{' '}
                        {usersQuery.data?.lastPage} (
                        {usersQuery.data?.totalCount} total users)
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={
                            !usersQuery.data ||
                            usersQuery.data.currentPage <= 1
                          }
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => p + 1)}
                          disabled={
                            !usersQuery.data ||
                            usersQuery.data.currentPage >=
                              usersQuery.data.lastPage
                          }
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Migration History (collapsible) */}
          {orgData?.found && (
            <Card>
              <CardHeader className="p-0">
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-6 cursor-pointer select-none hover:bg-accent/50 rounded-t-lg transition-colors"
                  onClick={() => setHistoryOpen((o) => !o)}
                  aria-expanded={historyOpen}
                >
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Migration History
                    <span className="text-muted-foreground font-normal text-sm">
                      ({orgData.migrationHistory.length} runs)
                    </span>
                  </CardTitle>
                  {historyOpen ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </CardHeader>
              {historyOpen && (
                <CardContent>
                  {orgData.migrationHistory.length === 0 ? (
                    <EmptyState
                      title="No migrations found"
                      description="No migrations have been run for this organization."
                    />
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Started By</TableHead>
                          <TableHead>Modules</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead aria-label="Actions"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orgData.migrationHistory.map((mh) => (
                          <TableRow key={mh.migrationId}>
                            <TableCell>
                              {new Date(mh.startedAt).toLocaleString()}
                            </TableCell>
                            <TableCell>{mh.startedByUserName}</TableCell>
                            <TableCell>{mh.totalModules}</TableCell>
                            <TableCell>
                              <StatusBadge status={mh.status} />
                            </TableCell>
                            <TableCell>
                              <Link
                                to="/migrations/$migrationId"
                                params={{ migrationId: mh.migrationId }}
                                className="text-sm text-primary hover:underline"
                              >
                                View →
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              )}
            </Card>
          )}
        </div>

        <UserDetailDialog
          user={selectedUser}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      </ViewerGuard>
    </AppLayout>
  );
}

